import {
  Building2,
  ClipboardList,
  FileText,
  FileUp,
  Flag,
  Handshake,
  LayoutDashboard,
  type LucideIcon,
  MailCheck,
  MessageSquare,
  Package,
  PackageSearch,
  Settings,
  Ship,
  ShoppingCart,
  Tags,
  TriangleAlert,
  Users as UsersIcon,
} from 'lucide-react';
import { isBuyerCapable, isSellerCapable } from '~/utils';

export interface NavItem {
  id: string;
  labelKey: string;
  icon: LucideIcon;
  to: string;
  external?: boolean;
}

export interface NavSection {
  id: string;
  headingKey?: string;
  isVisible: (role: string | null | undefined) => boolean;
  items: NavItem[];
}

const isTenantAdmin = (role: string | null | undefined): boolean =>
  role === 'TENANT_ADMIN';

const isPlatformAdmin = (role: string | null | undefined): boolean =>
  role === 'PLATFORM_ADMIN';

export const navSections: NavSection[] = [
  {
    id: 'overview',
    isVisible: (role) =>
      isBuyerCapable(role) ||
      isSellerCapable(role) ||
      isTenantAdmin(role) ||
      isPlatformAdmin(role),
    items: [
      {
        id: 'dashboard',
        labelKey: 'nav.dashboard',
        icon: LayoutDashboard,
        to: '/dashboard',
      },
    ],
  },
  {
    id: 'selling',
    headingKey: 'nav.section.selling',
    isVisible: isSellerCapable,
    items: [
      {
        id: 'my-products',
        labelKey: 'nav.my_products',
        icon: Package,
        to: '/my-products',
      },
      {
        id: 'seller-quotes',
        labelKey: 'nav.seller_quotes',
        icon: FileText,
        to: '/seller-quotes',
      },
      {
        id: 'seller-orders',
        labelKey: 'nav.seller_orders',
        icon: Package,
        to: '/seller-orders',
      },
      {
        id: 'seller-price-negotiations',
        labelKey: 'nav.seller_price_negotiations',
        icon: Handshake,
        to: '/seller-price-negotiations',
      },
      {
        id: 'inbox',
        labelKey: 'nav.inbox',
        icon: MessageSquare,
        to: '/seller-messages',
      },
    ],
  },
  {
    id: 'buying',
    headingKey: 'nav.section.buying',
    isVisible: isBuyerCapable,
    items: [
      {
        id: 'buy-lists',
        labelKey: 'nav.buy_lists',
        icon: ClipboardList,
        to: '/buy-lists',
      },
      {
        id: 'inventories',
        labelKey: 'nav.inventories',
        icon: Package,
        to: '/buyer-inventory',
      },
      { id: 'cart', labelKey: 'nav.cart', icon: ShoppingCart, to: '/cart' },
      {
        id: 'request-for-quotes',
        labelKey: 'nav.request_for_quotes',
        icon: FileText,
        to: '/request-for-quotes',
      },
      {
        id: 'buyer-orders',
        labelKey: 'nav.buyer_orders',
        icon: Package,
        to: '/buyer-orders',
      },
      {
        id: 'buyer-price-negotiations',
        labelKey: 'nav.buyer_price_negotiations',
        icon: Handshake,
        to: '/buyer-price-negotiations',
      },
      {
        id: 'messages',
        labelKey: 'nav.messages',
        icon: MessageSquare,
        to: '/buyer-messages',
      },
    ],
  },

  {
    id: 'general',
    isVisible: (role) => isBuyerCapable(role) || isSellerCapable(role),
    items: [
      {
        id: 'logistics',
        labelKey: 'nav.logistics',
        icon: Ship,
        to: '/sea-freight',
      },
      {
        id: 'settings',
        labelKey: 'nav.settings',
        icon: Settings,
        to: '/settings',
      },
    ],
  },
  {
    id: 'tenant-admin',
    isVisible: isTenantAdmin,
    items: [
      { id: 'users', labelKey: 'nav.users', icon: UsersIcon, to: '/users' },
    ],
  },
  {
    id: 'linked-organizations',
    isVisible: (role) =>
      isBuyerCapable(role) || isSellerCapable(role) || isTenantAdmin(role),
    items: [
      {
        id: 'linked-organizations',
        labelKey: 'nav.linked_organizations',
        icon: Building2,
        to: '/linked-organizations',
      },
    ],
  },
  {
    id: 'platform-admin',
    isVisible: isPlatformAdmin,
    items: [
      {
        id: 'organizations',
        labelKey: 'nav.organizations',
        icon: Building2,
        to: '/admin/organizations',
      },
      {
        id: 'inventory-classification',
        labelKey: 'nav.inventory_classification',
        icon: Tags,
        to: '/admin/inventory-classification',
      },
      {
        id: 'job-outbox',
        labelKey: 'nav.job_outbox',
        icon: TriangleAlert,
        to: '/admin/job-outbox',
      },
      {
        id: 'email-delivery',
        labelKey: 'nav.email_delivery',
        icon: MailCheck,
        to: '/admin/email-delivery',
      },
      {
        id: 'ufp-inventory',
        labelKey: 'nav.ufp_inventory',
        icon: PackageSearch,
        to: '/admin/ufp-inventory',
      },
      {
        id: 'product-import',
        labelKey: 'nav.product_import',
        icon: FileUp,
        to: '/admin/product-import',
      },
      {
        id: 'feature-flags',
        labelKey: 'nav.feature_flags',
        icon: Flag,
        to: '/togglz-console',
        external: true,
      },
    ],
  },
];

export const getVisibleNavSections = (
  role: string | null | undefined
): NavSection[] => navSections.filter((section) => section.isVisible(role));

export const findNavItemByPath = (pathname: string): NavItem | undefined =>
  navSections
    .flatMap((section) => section.items)
    .find((item) => item.to === pathname);

export const canAccessNavPath = (
  role: string | null | undefined,
  pathname: string
): boolean =>
  navSections.some(
    (section) =>
      section.isVisible(role) &&
      section.items.some((item) => item.to === pathname)
  );
